import {ConfigObjectType, HierarchyMetadata} from '@wallaby-ui/core';
import * as React from 'react';
import {ReactNode, useCallback, useMemo} from 'react';

import {JustTooltipFormatter} from './JustTooltipFormatter';
import {
  PhRecommendationDrpBaseFormatter,
  DRP_FLEX_COLUMN,
  DRP_CHIP_GAP,
  DRP_LINE_HEIGHT,
  DRP_PAD_Y,
  PhDrugRelatedProblemDto,
} from './PhRecommendationDrpBaseFormatter';
import {useMedicationAtcs, useMedications} from '../../../containers';
import {AG_GRID_COMPATIBILITY} from '../LightTable';
import {MerieSchema, WallabyCoreSchema} from 'src/js/common/types';

export const PhRecommendationDrpTextFormatter: React.FC<{
  problems: PhDrugRelatedProblemDto[];
  getText: (drp: PhDrugRelatedProblemDto) => string;
  dividerWidthOffset: number;
}> = ({problems, getText, dividerWidthOffset}) => {
  return (
    <PhRecommendationDrpBaseFormatter
      dividerWidthOffset={dividerWidthOffset}
      problems={problems}
      renderContent={(drp, drpHeight) => {
        const text = getText(drp);
        return (
          <div
            data-name='drp-text-formatter-item'
            style={{
              maxWidth: '100%',
              height: drpHeight,
              marginTop: -DRP_CHIP_GAP / 2,
              marginBottom: DRP_CHIP_GAP / 2,
              position: 'relative',
            }}
          >
            <JustTooltipFormatter
              {...AG_GRID_COMPATIBILITY}
              style={{
                lineHeight: `${DRP_LINE_HEIGHT + DRP_PAD_Y + DRP_CHIP_GAP}px`,
                display: 'inline-block',
                height: 'fit-content',
              }}
              singleLine
              textStyle={{
                paddingBlock: 0,
                whiteSpace: 'unset',
                lineClamp: drp.causes.length,
                display: '-webkit-box',
                WebkitLineClamp: drp.causes.length,
                WebkitBoxOrient: 'vertical',
              }}
              value={text || '-'}
              tooltipOverride={text}
            />
          </div>
        );
      }}
    />
  );
};

type PhRecommendationDrpListFormatterProps<T> = {
  problems: PhDrugRelatedProblemDto[];
  getItems: (drp: PhDrugRelatedProblemDto) => T[];
  renderItem: (item: T, index: number) => ReactNode;
  dividerWidthOffset: number;
};
export const PhRecommendationDrpListFormatter = <T,>(props: PhRecommendationDrpListFormatterProps<T>) => {
  const {problems, getItems, renderItem, dividerWidthOffset} = props;
  return (
    <PhRecommendationDrpBaseFormatter
      dividerWidthOffset={dividerWidthOffset}
      problems={problems}
      renderContent={(drp, drpHeight) => {
        const items = getItems(drp) ?? [];
        return (
          <div data-name='drp-list-formatter-item' style={{...DRP_FLEX_COLUMN, rowGap: DRP_CHIP_GAP, height: drpHeight}}>
            {items.length > 0 ? items.map((item, index) => renderItem(item, index)) : '-'}
          </div>
        );
      }}
    />
  );
};

export type PhRecDRP = MerieSchema.components['schemas']['DrugRelatedProblemDto'];
export type MedicationType = WallabyCoreSchema.components['schemas']['MedicationExportDto'];
export type MedicationAtc = WallabyCoreSchema.components['schemas']['AtcHierarchyNodeWithParentDto'] & HierarchyMetadata;
export type GetDRPMedicationDataProps = Pick<PhRecDRP, 'medicationId' | 'medicationUserInput' | 'medicationAtcId' | 'medicationName'>;
export type DRPMedicationData = {
  code: string;
  atcLabel?: string;
  name: string;
  strength?: string;
  fullMedicationLabel: string;
};
export function getDRPMedicationDataRaw(props: {
  drp: GetDRPMedicationDataProps;
  medicationsMap: Record<string, MedicationType>;
  medicationAtcMap: Record<string, MedicationAtc>;
}): DRPMedicationData {
  const {drp, medicationsMap, medicationAtcMap} = props;
  let medicationData: Omit<DRPMedicationData, 'fullMedicationLabel'>;
  if (drp.medicationId) {
    // known medication
    const userMedication = medicationsMap[drp.medicationId] as MedicationType | undefined;
    const medicationAtc = medicationAtcMap[drp.medicationAtcId] as MedicationAtc | undefined;
    medicationData = {
      code: medicationAtc?.code ?? '',
      name: userMedication?.name ?? drp.medicationName ?? '',
    };
  } else {
    // custom user string
    const medicationAtc = medicationAtcMap[drp.medicationAtcId] as MedicationAtc | undefined;
    medicationData = {
      code: medicationAtc?.code ?? '',
      atcLabel: medicationAtc?.label ?? '',
      name: drp.medicationUserInput ?? '',
    };
  }
  const {atcLabel, code, name, strength} = medicationData;
  return {
    ...medicationData,
    fullMedicationLabel: [
      atcLabel || code,
      [name, strength].filter((v) => v).join(' '), //
    ]
      .filter((v) => v)
      .join(' - '),
  };
}

export function useDRPMedicationData(config: ConfigObjectType) {
  // medications
  const {data: medications, medicationsLoading} = useMedications(config, 'MEDICINAL_PRODUCT');
  const medicationsMap = useMemo(() => {
    const acc = {} as Record<string, MedicationType>;
    medications.forEach((v) => {
      acc[v.id] = v;
    });
    return acc;
  }, [medications]);
  // medicationAtcs
  const {data: allMedicationAtcs, isLoading: medicationAtcsLoading} = useMedicationAtcs<MedicationAtc>(config);
  const medicationAtcMap = useMemo(() => {
    const acc: Record<string, MedicationAtc> = {};
    allMedicationAtcs.forEach((v) => {
      acc[v.id] = v;
    });
    return acc;
  }, [allMedicationAtcs]);
  // getDRPMedicationData
  const getDRPMedicationData = useCallback(
    (drp: GetDRPMedicationDataProps): DRPMedicationData => {
      return getDRPMedicationDataRaw({drp, medicationsMap, medicationAtcMap});
    },
    [medicationsMap, medicationAtcMap]
  );

  const isLoading = useMemo(() => medicationsLoading || medicationAtcsLoading, [medicationsLoading, medicationAtcsLoading]);

  return {
    getDRPMedicationData,
    medications,
    allMedicationAtcs,
    isLoading,
  };
}
